import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  ButtonBase,
  IconButton,
  Tooltip,
  Button,
  TextField,
  Menu,
  MenuItem,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import TaskIcon from '@mui/icons-material/Assignment';
import { format } from 'date-fns';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import AgendaDeleteConfirmationDialog from '../myEventDashboard/agendaTab/AgendaDeleteConfirmationDialog';
import {
  findNearestSlotIdxAfter,
  findNearestSlotIdxBefore,
  computeSlotIndices,
  assignColumns,
  TIME_REGEX_SECONDS,
  getEventBoxHeight
} from '../../utils/timeslotCalculationLogic';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import axios from 'axios';
import { getBackendUserUrl } from '../../utils/utils';

const ITEM_TYPE = 'AGENDA_ITEM';

function DraggableAgendaItem({ item, children, onDropAgenda }) {
  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { ...item },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  return (
    <div ref={drag} style={{ opacity: isDragging ? 0.5 : 1, width: '100%', height: '100%' }}>
      {children}
    </div>
  );
}

function DroppableCell({ children, slot, space, onDropAgenda, selectedDateStr, ...props }) {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ITEM_TYPE,
    drop: (draggedItem) => {
      const newStartTime = slot;
      const [oldStartHour, oldStartMinute] = draggedItem.startTime.split(':').map(Number);
      const [oldEndHour, oldEndMinute] = draggedItem.endTime.split(':').map(Number);
      const duration = (oldEndHour * 60 + oldEndMinute) - (oldStartHour * 60 + oldStartMinute);
      const [newStartHour, newStartMinute] = slot.split(':').map(Number);
      let newEndHour = newStartHour;
      let newEndMinute = newStartMinute + duration;
      if (newEndMinute >= 60) {
        newEndHour += Math.floor(newEndMinute / 60);
        newEndMinute = newEndMinute % 60;
      }
      const newEndTime = `${String(newEndHour).padStart(2, '0')}:${String(newEndMinute).padStart(2, '0')}`;
      onDropAgenda && onDropAgenda({
        ...draggedItem,
        startTime: newStartTime,
        endTime: newEndTime,
        spaceId: space.spaceId,
        date: selectedDateStr,
      });
    },
    canDrop: () => true,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });
  return (
    <ButtonBase
      ref={drop}
      {...props}
      sx={{
        ...props.sx,
        background: isOver && canDrop ? '#dbeafe' : '#fff',
      }}
    >
      {children}
    </ButtonBase>
  );
}

const AgendaItemMenu = ({ anchorEl, onClose, onAddTask, onViewDetails }) => (
  <Menu
    anchorEl={anchorEl}
    open={Boolean(anchorEl)}
    onClose={onClose}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
  >
    <MenuItem onClick={onAddTask}>
      <TaskIcon sx={{ mr: 1, fontSize: 20 }} />
      Add Task
    </MenuItem>
    <MenuItem onClick={onViewDetails}>
      <EventIcon sx={{ mr: 1, fontSize: 20 }} />
      View Details
    </MenuItem>
  </Menu>
);

const AgendaDetailsDialog = ({ open, onClose, agenda, tasks }) => {
  const taskStats = {
    completed: tasks.filter(t => t.status === 'completed').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    incomplete: tasks.filter(t => t.status === 'incomplete').length,
  };

  const getStatusColor = () => {
    if (tasks.length === 0) return 'grey';
    if (taskStats.completed === tasks.length) return 'success.main';
    if (taskStats.completed === 0) return 'error.main';
    return 'warning.main';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Badge
            variant="dot"
            sx={{ 
              '& .MuiBadge-badge': { 
                bgcolor: getStatusColor(),
                width: 12,
                height: 12,
                borderRadius: '50%'
              }
            }}
          >
            <EventIcon sx={{ fontSize: 24, color: '#6d7eea' }} />
          </Badge>
          <Typography variant="h6" sx={{ flex: 1 }}>
            {agenda.name}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Time & Location
          </Typography>
          <Typography variant="body1">
            {format(new Date(agenda.startTime), 'h:mm a')} - {format(new Date(agenda.endTime), 'h:mm a')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {agenda.spaceId}
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Description
          </Typography>
          <Typography variant="body1">
            {agenda.description || 'No description provided.'}
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Tasks ({tasks.length})
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ 
              p: 2, 
              bgcolor: 'success.light',
              borderRadius: 1,
              flex: 1,
              textAlign: 'center'
            }}>
              <Typography variant="h6" color="success.dark">
                {taskStats.completed}
              </Typography>
              <Typography variant="body2" color="success.dark">
                Completed
              </Typography>
            </Box>
            <Box sx={{ 
              p: 2, 
              bgcolor: 'warning.light',
              borderRadius: 1,
              flex: 1,
              textAlign: 'center'
            }}>
              <Typography variant="h6" color="warning.dark">
                {taskStats.inProgress}
              </Typography>
              <Typography variant="body2" color="warning.dark">
                In Progress
              </Typography>
            </Box>
            <Box sx={{ 
              p: 2, 
              bgcolor: 'error.light',
              borderRadius: 1,
              flex: 1,
              textAlign: 'center'
            }}>
              <Typography variant="h6" color="error.dark">
                {taskStats.incomplete}
              </Typography>
              <Typography variant="body2" color="error.dark">
                Incomplete
              </Typography>
            </Box>
          </Box>
          {tasks.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              {tasks.map(task => (
                <Box
                  key={task.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <TaskIcon sx={{ 
                    color: task.status === 'completed' ? 'success.main' :
                           task.status === 'in_progress' ? 'warning.main' :
                           'error.main'
                  }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1">
                      {task.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {task.status}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', mt: 2 }}
            >
              No tasks assigned to this agenda item.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const DayView = ({
  spaces = [],
  items = [],
  tasks = [],
  date,
  onDeleteSpace,
  onAddSpace,
  editingSpaceId,
  editingSpaceName,
  onStartEditSpace,
  onSaveEditSpace,
  onCancelEditSpace,
  onEditSpaceNameChange,
  onCellClick,
  timeSlots,
  onEditItem,
  onAgendaDeleted,
  onAgendaUpdated,
  onOptimisticAgendaUpdate,
}) => {
  const [hoveredSpace, setHoveredSpace] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedAgenda, setSelectedAgenda] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  useEffect(() => {
    console.log('DayView props:', {
      spaces,
      items,
      date
    });
  }, [spaces, items, date]);
  // Ensure spaces is always an array
  const safeSpaces = Array.isArray(spaces) ? spaces : [];

  // Ensure items and tasks are always arrays
  const safeItems = Array.isArray(items) ? items : [];
  const safeTasks = Array.isArray(tasks) ? tasks : [];

  // Filter items for the selected date
  const selectedDateStr = date ? date.format ? date.format('YYYY-MM-DD') : date : null;
  const filteredItems = selectedDateStr
    ? safeItems.filter(item => item.date === selectedDateStr)
    : safeItems;
  console.log('filteredItems', filteredItems);
  const handleAgendaContextMenu = (event, agenda) => {
    event.preventDefault();
    setSelectedAgenda(agenda);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setSelectedAgenda(null);
  };

  const handleAddTask = () => {
    handleCloseMenu();
  };

  const handleViewDetails = () => {
    setDetailsDialogOpen(true);
    handleCloseMenu();
  };

  const handleClickAway = (spaceId) => {
    if (editingSpaceName.trim()) {
      onSaveEditSpace(spaceId);
    } else {
      onCancelEditSpace();
    }
  };

  const handleDeleteClick = (e, item) => {
    e.stopPropagation();
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteSuccess = (deletedItemId) => {
    // Notify parent component to update its state
    onAgendaDeleted(deletedItemId);
    // Close the dialog
    handleDeleteCancel();
  };

  // Move onDropAgenda inside DayView so it can access onAgendaUpdated
  async function onDropAgenda(updatedItem) {
    // Optimistic UI: save previous state
    const prevItems = items;
    // Format startTime and endTime as 'HH:mm:ss+00'
    const formattedStartTime = `${updatedItem.startTime}:00+00`;
    const formattedEndTime = `${updatedItem.endTime}:00+00`;
    const patchPayload = {
      name: updatedItem.name,
      description: updatedItem.description,
      start_time: formattedStartTime,
      end_time: formattedEndTime,
      date: updatedItem.date,
    };
    // Optimistically update UI
    if (onOptimisticAgendaUpdate) {
      onOptimisticAgendaUpdate(currentItems => currentItems.map(item =>
        item.agendaId === updatedItem.agendaId
          ? { ...item, ...updatedItem, startTime: formattedStartTime, endTime: formattedEndTime }
          : item
      ));
    }
    try {
      console.log('PATCH agenda item payload:', patchPayload);
      await axios.patch(`${getBackendUserUrl()}/agenda/agenda_items/${updatedItem.agendaId}`, patchPayload);
      // Refresh agenda items after successful update
      if (onAgendaUpdated) onAgendaUpdated();
    } catch (error) {
      // Rollback UI
      if (onOptimisticAgendaUpdate) {
        onOptimisticAgendaUpdate(() => prevItems);
      }
      alert('Failed to update agenda item. Please try again.');
      console.error('Failed to update agenda item:', error);
    }
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ overflowX: 'auto', width: '100%' }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: `120px repeat(${safeSpaces.length}, 1fr)`,
          borderRadius: 2,
          border: '1px solid #e3e6f0',
          background: '#fff',
        }}>
          {/* Header Row */}
          <Box sx={{
            p: 1,
            background: '#fff',
            border: '1px solid #e3e6f0',
            borderBottom: 'none',
            borderTopLeftRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Button
              variant="outlined"
              size="small"
              onClick={onAddSpace}
              sx={{ borderRadius: 2, fontWeight: 600, color: '#6d7eea', borderColor: '#6d7eea', '&:hover': { bgcolor: '#edeaff', borderColor: '#6d7eea' } }}
            >
              Add Space
            </Button>
          </Box>

          {/* Space Headers */}
          {safeSpaces.map(space => (
            <Box
              key={space.id}
              onMouseEnter={() => setHoveredSpace(space.id)}
              onMouseLeave={() => setHoveredSpace(null)}
              sx={{
                p: 1,
                background: '#fff',
                border: '1px solid #e3e6f0',
                borderBottom: 'none',
                position: 'relative',
                minHeight: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {editingSpaceId === space.id ? (
                <ClickAwayListener onClickAway={() => handleClickAway(space.id)}>
                  <TextField
                    size="small"
                    value={editingSpaceName}
                    onChange={onEditSpaceNameChange}
                    autoFocus
                    sx={{ 
                      minWidth: 120,
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && editingSpaceName.trim()) {
                        onSaveEditSpace(space.id);
                      } else if (e.key === 'Escape') {
                        onCancelEditSpace();
                      }
                    }}
                  />
                </ClickAwayListener>
              ) : (
                <Typography
                  variant="subtitle1"
                  onClick={() => !space.isTemporary && onStartEditSpace(space.id, space.name)}
                  sx={{
                    fontWeight: 500,
                    cursor: space.isTemporary ? 'default' : 'pointer',
                    color: space.isTemporary ? 'text.secondary' : 'inherit',
                    fontStyle: space.isTemporary ? 'italic' : 'normal',
                    '&:hover': { color: space.isTemporary ? 'inherit' : '#6d7eea' }
                  }}
                >
                  {space.name}
                </Typography>
              )}

              {!space.isTemporary && hoveredSpace === space.id && (
                <Tooltip title="Delete Space">
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      onDeleteSpace(space.spaceId);
                    }}
                    sx={{
                      ml: 1,
                      color: '#bdbdbd',
                      '&:hover': { color: '#e57373', bgcolor: 'transparent' },
                      position: 'absolute',
                      right: 4,
                      top: 4
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}

          {/* Time slots and cells */}
          {timeSlots.map((slot, rowIdx) => (
            <React.Fragment key={slot}>
              {/* Time label */}
              <Box sx={{
                p: 1,
                background: '#fff',
                border: '1px solid #e3e6f0',
                color: '#888',
                fontWeight: 500,
                fontSize: 14,
                textAlign: 'center',
              }}>{slot}</Box>

              {/* Cells for each space */}
              {safeSpaces.map(space => {
                // For this cell (space, slot), find all items that overlap with this slot
                const overlappingItems = filteredItems.filter(item => {
                  if (!item.startTime || !item.endTime) return false;
                  const startMatch = item.startTime.match(TIME_REGEX_SECONDS);
                  const endMatch = item.endTime.match(TIME_REGEX_SECONDS);
                  if (!startMatch || !endMatch) return false;
                  const startTime = `${startMatch[1]}:${startMatch[2]}`;
                  const endTime = `${endMatch[1]}:${endMatch[2]}`;
                  const startIdx = findNearestSlotIdxAfter(startTime, timeSlots);
                  const endIdx = findNearestSlotIdxBefore(endTime, timeSlots);
                  return (
                    item.spaceId === space.spaceId &&
                    startIdx <= rowIdx && rowIdx <= endIdx
                  );
                });

                // Only render items that start at this slot
                const itemsStartingHere = overlappingItems.filter(item => {
                  const startMatch = item.startTime.match(TIME_REGEX_SECONDS);
                  if (!startMatch) return false;
                  const startTime = `${startMatch[1]}:${startMatch[2]}`;
                  const startIdx = findNearestSlotIdxAfter(startTime, timeSlots);
                  return startIdx === rowIdx;
                });

                // Assign columns only for itemsStartingHere
                let itemsWithColumns = itemsStartingHere;
                let maxColumns = itemsStartingHere.length;
                if (itemsStartingHere.length > 1) {
                  const { itemsWithColumns: assigned, maxColumns: maxCols } = assignColumns(itemsStartingHere);
                  itemsWithColumns = assigned;
                  maxColumns = maxCols;
                }

                return (
                  <DroppableCell
                    key={space.id + slot}
                    slot={slot}
                    space={space}
                    onDropAgenda={onDropAgenda}
                    selectedDateStr={selectedDateStr}
                    onClick={() => onCellClick(space.spaceId, slot)}
                    sx={{
                      width: '100%',
                      height: 48,
                      border: '1px solid #e3e6f0',
                      transition: 'background 0.2s',
                      '&:hover': { bgcolor: '#edeaff' },
                      display: 'block',
                      position: 'relative',
                      p: 0,
                      overflow: 'visible',
                    }}
                  >
                    {itemsWithColumns.map((item, itemIndex) => {
                      const startMatch = item.startTime.match(TIME_REGEX_SECONDS);
                      const endMatch = item.endTime.match(TIME_REGEX_SECONDS);
                      if (!startMatch || !endMatch) return null;
                      const now = new Date();
                      const startDate = new Date(now);
                      startDate.setHours(parseInt(startMatch[1]));
                      startDate.setMinutes(parseInt(startMatch[2]));
                      const endDate = new Date(now);
                      endDate.setHours(parseInt(endMatch[1]));
                      endDate.setMinutes(parseInt(endMatch[2]));
                      // Use helper for height
                      const heightPx = getEventBoxHeight(item, timeSlots);
                      // Only assign columns if more than one item
                      const colIndex = itemsWithColumns.length > 1 ? (item._column || 0) : 0;
                      const itemWidth = itemsWithColumns.length > 1 ? `calc(${100 / maxColumns}% - 4px)` : '100%';
                      const leftPosition = itemsWithColumns.length > 1 ? `calc(${(100 / maxColumns) * colIndex}% + 2px)` : '0';
                      const agendaKey = item.agendaId || item.id;

                      return (
                        <DraggableAgendaItem key={agendaKey} item={item} onDropAgenda={onDropAgenda}>
                          <Box
                            key={agendaKey}
                            onMouseEnter={() => setHoveredItem(agendaKey)}
                            onMouseLeave={() => setHoveredItem(null)}
                            sx={{
                              position: 'absolute',
                              left: leftPosition,
                              width: itemWidth,
                              top: 0,
                              height: `${heightPx}px`,
                              zIndex: 2,
                              p: 1,
                              borderRadius: 1,
                              bgcolor: '#e0e7ff',
                              color: '#3730a3',
                              border: '1.5px solid #a5b4fc',
                              boxShadow: '0 2px 8px 0 rgba(0,0,0,0.04)',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                              cursor: 'pointer',
                              '&:hover': {
                                filter: 'brightness(0.98)'
                              }
                            }}
                            onClick={e => {
                              e.stopPropagation();
                              onEditItem(item);
                            }}
                          >
                            {hoveredItem === agendaKey && (
                              <Tooltip title="Delete Agenda Item">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleDeleteClick(e, item)}
                                  sx={{
                                    position: 'absolute',
                                    right: 4,
                                    top: 4,
                                    color: '#bdbdbd',
                                    '&:hover': { color: '#e57373', bgcolor: 'transparent' },
                                    zIndex: 3,
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {heightPx >= 20 && (
                              <Typography
                                noWrap
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  color: '#6d7eea',
                                  textAlign: 'center',
                                  borderRadius: 1,
                                  bgcolor: '#edeaff',
                                  px: 1,
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  flexShrink: 1,
                                  lineHeight: 1,
                                  fontSize: '0.85rem',
                                }}
                              >
                                {item.name?.[0] || '?'}
                              </Typography>
                            )}
                            <Box sx={{
                              flex: 1,
                              overflow: 'hidden',
                              minWidth: 0,
                              maxWidth: '100%',
                              height: '100%',
                              width: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              position: 'relative',
                            }}>
                              <Typography
                                noWrap
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  mb: 0.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  minWidth: 0,
                                  maxWidth: '100%',
                                  width: '100%',
                                  flexShrink: 1,
                                  lineHeight: 1,
                                  fontSize: '0.85rem',
                                }}
                              >
                                {item.name}
                              </Typography>
                              <Typography
                                noWrap
                                variant="caption"
                                sx={{
                                  color: '#222',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  minWidth: 0,
                                  maxWidth: '100%',
                                  width: '100%',
                                  flexShrink: 1,
                                  lineHeight: 1,
                                  fontSize: '0.85rem',
                                }}
                              >
                                {format(startDate, 'h:mm a')} - {format(endDate, 'h:mm a')}
                              </Typography>
                            </Box>
                          </Box>
                        </DraggableAgendaItem>
                      );
                    })}
                  </DroppableCell>
                );
              })}
            </React.Fragment>
          ))}
        </Box>

        <AgendaItemMenu
          anchorEl={menuAnchorEl}
          onClose={handleCloseMenu}
          onAddTask={handleAddTask}
          onViewDetails={handleViewDetails}
        />

        {selectedAgenda && (
          <AgendaDetailsDialog
            open={detailsDialogOpen}
            onClose={() => setDetailsDialogOpen(false)}
            agenda={selectedAgenda}
            tasks={safeTasks.filter(task => task.agendaId === selectedAgenda.id)}
          />
        )}

        <AgendaDeleteConfirmationDialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteSuccess}
          agendaItem={itemToDelete}
        />
      </Box>
    </DndProvider>
  );
};

export default DayView; 